import type { LocationQuery, RouteLocationNormalized, RouteLocationRaw, Router } from 'vue-router'
import { useToast } from 'vue-toastification'
import { useProfileStore } from '@/stores/profile'
import { getProfile, hasValidProfile, isFullyAuthenticatedForApp, isSSOUser } from '@/utils/helper'

declare module 'vue-router' {
  interface RouteMeta {
    /** Signed-in patients only (React's ProtectedRoute). */
    requiresAuth?: boolean
    /** Intake/complaint routes: a profile and a live session (React's SSORoute). */
    sso?: boolean
    /** Sign-in pages: a signed-in patient is sent on (React's RedirectIfAuth). */
    redirectIfAuth?: boolean
  }
}

const LOGIN_PATH = '/account/login'

/** Query param carrying the page to return to (React passed `state.from`; vue-router has no location state). */
const REDIRECT_QUERY = 'redirect'

export const SESSION_EXPIRED_TOAST = 'Your session has expired. Please sign in again to continue.'

/**
 * A same-origin app path from `?redirect=`, or null. Only `/...` paths are accepted; `//host`,
 * `/\host`, absolute URLs and anything that resolves to another origin are rejected, so the
 * param can't be used as an open redirect.
 */
export function safeRedirectPath(value: unknown): string | null {
  const raw = Array.isArray(value) ? value[0] : value
  if (typeof raw !== 'string' || !raw.startsWith('/')) return null
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null
  // Control characters (tab, newline) are stripped by URL parsers and can smuggle in `//`.
  if ([...raw].some((ch) => ch.charCodeAt(0) < 0x20 || ch.charCodeAt(0) === 0x7f)) return null
  try {
    const base = window.location.origin
    const url = new URL(raw, base)
    if (url.origin !== base) return null
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return null
  }
}

/** `?redirect=` as a replace-navigation, or `/`. Split into path/query/hash: a `{ path }` ignores a query string. */
function redirectTarget(query: LocationQuery): RouteLocationRaw {
  const target = safeRedirectPath(query[REDIRECT_QUERY])
  if (!target) return { path: '/', replace: true }
  const url = new URL(target, window.location.origin)
  const targetQuery: LocationQuery = {}
  for (const key of new Set(url.searchParams.keys())) {
    const values = url.searchParams.getAll(key)
    targetQuery[key] = values.length > 1 ? values : (values[0] ?? '')
  }
  return { path: url.pathname, query: targetQuery, hash: url.hash, replace: true }
}

function toLogin(to: RouteLocationNormalized): RouteLocationRaw {
  return { path: LOGIN_PATH, query: { [REDIRECT_QUERY]: to.fullPath }, replace: true }
}

function hasLiveSession(): boolean {
  return !!sessionStorage.getItem('config') && !!sessionStorage.getItem('completeAuth')
}

/**
 * The portal's auth guards, as one `beforeEach`. Routes opt in with `meta.requiresAuth`,
 * `meta.sso` or `meta.redirectIfAuth` (nested routes inherit their parents' meta).
 */
export function installAuthGuards(router: Router): void {
  router.beforeEach((to) => {
    if (to.meta.requiresAuth) {
      const profile = useProfileStore()
      const stored = getProfile()
      if (stored && isSSOUser(stored)) {
        localStorage.removeItem('portal-profile')
        profile.logout()
        return toLogin(to)
      }
      if (!isFullyAuthenticatedForApp(profile.profileAuthInfo, profile.currentPatient)) {
        return toLogin(to)
      }
    }

    if (to.meta.sso) {
      const stored = getProfile()
      const hasProfile = !!stored && hasValidProfile(stored)
      // React's SSORoute went to `state.from` or `/`; nothing ever set `from` for these routes,
      // so it was always `/`. `?redirect=` is not honoured here.
      if (!hasProfile) {
        return { path: '/', replace: true }
      }
      if (!hasLiveSession()) {
        useToast().error(SESSION_EXPIRED_TOAST)
        return { path: LOGIN_PATH, replace: true }
      }
    }

    if (to.meta.redirectIfAuth) {
      const profile = useProfileStore()
      if (isFullyAuthenticatedForApp(profile.profileAuthInfo, profile.currentPatient)) {
        return redirectTarget(to.query)
      }
    }

    return true
  })
}
